import { patch } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { ExtraWorkedTimeRequestStatus } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { TIMEKEEPING_REASON_CONSTANTS } from "../Timekeeping/constants";
import { fullShiftExtraWorkedTimeRequestEntitySchema } from "./fullShiftExtraWorkedTimeRequestSchema";

const EXTRA_WORKED_TIME_REQUESTS_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/extra-worked-time-requests`;

const patchFullShiftExtraWorkedTimeRequestBodySchema = z.object({
  start: z.string().datetime().optional(), // ISO 8601 — updated adjusted clock-in
  reason: z
    .string()
    .min(TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS)
    .max(TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS)
    .optional(),
  status: z.nativeEnum(ExtraWorkedTimeRequestStatus).optional(),
});

const patchFullShiftExtraWorkedTimeRequestResponseSchema = z.object({
  data: fullShiftExtraWorkedTimeRequestEntitySchema,
});

export interface PatchFullShiftExtraWorkedTimeRequestBody extends z.infer<
  typeof patchFullShiftExtraWorkedTimeRequestBodySchema
> {
  extraWorkedTimeRequestId: string;
}

export type PatchFullShiftExtraWorkedTimeRequestResponse = z.infer<
  typeof patchFullShiftExtraWorkedTimeRequestResponseSchema
>;

export function usePatchFullShiftExtraWorkedTimeRequest(
  options: UseMutationOptions<
    PatchFullShiftExtraWorkedTimeRequestResponse,
    AxiosError,
    PatchFullShiftExtraWorkedTimeRequestBody
  > = {}
): UseMutationResult<
  PatchFullShiftExtraWorkedTimeRequestResponse,
  AxiosError,
  PatchFullShiftExtraWorkedTimeRequestBody
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      extraWorkedTimeRequestId,
      ...data
    }: PatchFullShiftExtraWorkedTimeRequestBody) => {
      const response = await patch({
        url: `${EXTRA_WORKED_TIME_REQUESTS_URL}/${extraWorkedTimeRequestId}`,
        data,
        requestSchema: patchFullShiftExtraWorkedTimeRequestBodySchema,
        responseSchema: patchFullShiftExtraWorkedTimeRequestResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PATCH_FULL_SHIFT_EXTRA_WORKED_TIME_REQUEST_FAILURE,
      userErrorMessage: "Failed to update your time adjustment. Please try again.",
    },
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: [EXTRA_WORKED_TIME_REQUESTS_URL] });
      options.onSuccess?.(data, variables, context);
    },
  });
}
